import { Center, Loader } from "@mantine/core"
import { useQuery } from "@tanstack/react-query"
import { useAuth } from "../../../../hooks/useAuth"
import { useCompanyApi } from "../../../../hooks/useCompanyApi"
import { fetchAdminModels, getAvailableModels } from "../../../../lib/api"
import { authClient } from "../../../../lib/auth-client"
import { queryKeys } from "../../../../lib/query-keys"
import { ModelsPanelView } from "./ModelsPanelView"

interface IProps {
  settings: Record<string, string>
  saving: boolean
  /** Platform (super-admin) mode — resolve models on platform keys, show the report gate. */
  isAdminOnly: boolean
  updateSetting: (key: string, value: string) => void
  save: (keys?: string[]) => void
}

/**
 * Smart wrapper for the Settings → Model selection panel. Loads the available LLM
 * models (rate-card + capability metadata) for the active scope — company keys in
 * tenant mode, platform keys in super-admin mode — and resolves whether the caller
 * may change model settings (a cost lever: owner/admin only; plain members read
 * only). The dumb {@link ModelsPanelView} renders the tiers + per-task overrides.
 */
export function ModelsPanel({ settings, saving, isAdminOnly, updateSetting, save }: IProps): React.JSX.Element {
  const { user } = useAuth()
  const company = useCompanyApi()
  const { data: activeMember } = authClient.useActiveMember()
  const orgId = user?.activeOrganizationId ?? null

  // Owner/admin (per-org, from Better Auth) or a global super-admin may change the
  // model keys; a plain member gets a read-only view. Mirrors the backend PUT gate.
  const orgRole = (activeMember as { role?: string } | null | undefined)?.role
  const canWrite = !!user?.isAdmin || orgRole === "owner" || orgRole === "admin"

  const { data } = useQuery({
    queryKey: queryKeys.llmModels(isAdminOnly ? null : orgId),
    queryFn: () => (isAdminOnly || !company ? fetchAdminModels() : getAvailableModels(company)),
    enabled: isAdminOnly || !!company,
  })

  // The whole panel structure (tiers + per-task rows) is driven by `defaults`, so
  // hold a brief loader until the models query resolves rather than flashing an
  // empty shell.
  if (!data) {
    return (
      <Center py="xl">
        <Loader />
      </Center>
    )
  }

  return (
    <ModelsPanelView
      models={data.models}
      defaults={data.defaults}
      settings={settings}
      saving={saving}
      canWrite={canWrite}
      isAdminOnly={isAdminOnly}
      updateSetting={updateSetting}
      save={save}
    />
  )
}
